// Third-party
import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';

// Components
import { Tabs } from '@/views/components/navigation';
import { ControlLinkItem } from '@/views/components/navigation';
import { LoadingSpinner } from '@/views/components/common';
import {
  ContactDetailsComparisons,
  ContactDetailsOmniRiskProfiles,
  ContactDetailsRiskProfiles,
  ContactNotes,
  ContactPlanAccounts,
  ContactPortfolios,
} from './tabs';

// Legacy component
import LegacyContactDetailsTabs from './LegacyContactDetailsTabs';

// Actions
import { CurrentContact } from '@/redux/modules/types/contacts';
import { GET_QUESTIONNAIRE } from '@/redux/modules/kyc-questionnaire';
import { selectIsLoadingSome } from '@/redux/modules/ui/loaders';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Types
import User from '@/types/user';
import Locale from '@/types/locale';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Comparison from '@/types/comparison';
import Activity from '@/types/activity';
import ReducedComparison from '@/types/reduced-comparison';
import ContactRiskProfile from '@/types/contact-risk-profile';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { NAV_PATHS } from '@/utils/constants';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '@/utils/permissions';

// TODO LEGACY DEPRECATION: Remove LegacyProps when we remove Legacy Components
type LegacyProps = {
  onLoadMoreActivityLogs: () => Promise<Activity[]>;
  onCreateComparison: () => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onRerunComparison: (comparison: ReducedComparison) => void;
  openingComparisonIds: string[];
  rerunningComparisonIds: string[];
};

type StateProps = {
  isGettingQuestionnaires: boolean;
};

type Props = LegacyProps &
  StateProps &
  RouteComponentProps<{ householdId?: string; contactId?: string }> & {
    user: User;
    locale: Locale;
    currentContact: CurrentContact;
    isOmniRiskProfile?: boolean;
    questionnaires: KycQuestionnaire[];
    onLoadMoreComparisons: () => Promise<Comparison[]>;
    onViewComparison: (comparison: ReducedComparison) => void;
    onSearchComparisons: (query: string) => Promise<ReducedComparison[]>;
    onAttachOmniRiskProfile: (profile: FormattedFullOmniRiskProfile) => void;
    onAttachRiskProfile: (profile: ContactRiskProfile) => void;
    onDeleteRiskProfile: (profile: ContactRiskProfile) => void;
    onEditRiskProfile: (profile: ContactRiskProfile) => void;
    onCreateRiskProfile: (questionnaire?: KycQuestionnaire) => void;
    loading: {
      portfolios: boolean;
      comparisons: boolean;
    };
    attachingRiskProfile: boolean;
    selectedRiskProfileForComparison?: ContactRiskProfile;
  };

enum ContactDetailsTab {
  Portfolios = 'Portfolios',
  Comparisons = 'Comparisons',
  RiskProfiles = 'RiskProfiles',
  OmniRiskProfiles = 'OmniRiskProfiles',
  PlanAccounts = 'PlanAccounts',
  Notes = 'Notes',
}

enum ContactDetailsPaths {
  Portfolios = 'portfolios',
  Comparisons = 'comparisons',
  RiskProfiles = 'risk-profiles',
  OmniRiskProfiles = 'omni-risk-profiles',
  PlanAccounts = 'plan-accounts',
  Notes = 'notes',
}

type State = {
  currentTab: string;
  showSpinner: boolean;
};

class ContactDetailsTabsV2 extends Component<Props> {
  DEFAULT_TAB = ContactDetailsTab.Portfolios;

  readonly state: State = {
    currentTab: this.DEFAULT_TAB,
    showSpinner: false,
  };

  componentDidMount() {
    const { location, isGettingQuestionnaires } = this.props;
    if (location.pathname.includes(ContactDetailsPaths.RiskProfiles) && isGettingQuestionnaires) {
      this.setState({ showSpinner: true });
    } else {
      this.setInitialTab();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.isGettingQuestionnaires !== this.props.isGettingQuestionnaires && !this.props.isGettingQuestionnaires) {
      this.setInitialTab();
      this.setState({ showSpinner: false });
    }

    if (prevProps.location.pathname !== this.props.location.pathname) {
      const selectedTab = this.getSelectedTabFromUrl();
      if (selectedTab !== this.state.currentTab) {
        this.setState({ currentTab: selectedTab });
      }
    }
  }

  setInitialTab = () => {
    const { currentTab } = this.state;
    const selectedTab = this.getSelectedTabFromUrl();
    if (!selectedTab) {
      const path = this.getContactPathUrl();
      const redirect = `${path}/${ContactDetailsPaths[currentTab as keyof typeof ContactDetailsTab]}`;
      this.props.history.replace(redirect);
      this.setState({ currentTab });
    } else {
      this.setState({ currentTab: selectedTab });
    }
  };

  getSelectedTabFromUrl = () => {
    const { location } = this.props;

    const selectedTab = this.getTabs().find(tab => location.pathname === tab.value);

    return selectedTab ? selectedTab.id : null;
  };

  getCurrentTab = () => this.getSelectedTabFromUrl() || this.DEFAULT_TAB;

  getContactPathUrl = () => {
    const { currentContact, match } = this.props;
    const { householdId, contactId } = match.params;

    if (!currentContact || !currentContact.contact._id) {
      return null;
    }

    if (householdId && contactId) {
      return NAV_PATHS.HOUSEHOLD_CONTACT_DETAILS.replace(':householdId', householdId).replace(':contactId', contactId);
    }

    return NAV_PATHS.CONTACT_DETAILS.replace(':contactId', currentContact.contact._id);
  };

  getTabs = () => {
    const { user, isGettingQuestionnaires, isOmniRiskProfile, questionnaires } = this.props;

    const path = this.getContactPathUrl();
    const i18nBasePath = 'ContactDetails.Content';

    if (!path) {
      return [];
    }

    return [
      {
        id: ContactDetailsTab.Portfolios,
        value: `${path}/${ContactDetailsPaths.Portfolios}`,
        label: formatMessage(`${i18nBasePath}.Tabs.Portfolios`),
        i18nPath: `${i18nBasePath}.Tabs.Portfolios`,
      },
      {
        id: ContactDetailsTab.Comparisons,
        value: `${path}/${ContactDetailsPaths.Comparisons}`,
        label: formatMessage(`${i18nBasePath}.Tabs.Comparisons`),
        i18nPath: `${i18nBasePath}.Tabs.Comparisons`,
      },
      {
        id: ContactDetailsTab.RiskProfiles,
        value: `${path}/${ContactDetailsPaths.RiskProfiles}`,
        label: formatMessage(`${i18nBasePath}.Tabs.RiskProfiles`),
        hidden:
          !userHasPermissionEnabled(user, PERMISSIONS.RISK_QUESTIONNAIRE, PERMISSIONS.RISK_PROFILE) ||
          !userHasPermission(user, PERMISSIONS.CONTACTS_RISK_PROFILES_TAB) ||
          isOmniRiskProfile ||
          isGettingQuestionnaires ||
          (!isGettingQuestionnaires && questionnaires.length === 0),
        i18nPath: `${i18nBasePath}.Tabs.RiskProfiles`,
      },
      {
        id: ContactDetailsTab.OmniRiskProfiles,
        value: `${path}/${ContactDetailsPaths.OmniRiskProfiles}`,
        label: formatMessage(`${i18nBasePath}.Tabs.RiskProfiles`),
        hidden: !isOmniRiskProfile,
        i18nPath: `${i18nBasePath}.Tabs.RiskProfiles`,
      },
      {
        id: ContactDetailsTab.PlanAccounts,
        value: `${path}/${ContactDetailsPaths.PlanAccounts}`,
        label: formatMessage(`${i18nBasePath}.Tabs.PlanAccounts`),
        hidden: !userHasPermission(user, PERMISSIONS.PLANNING),
        i18nPath: `${i18nBasePath}.Tabs.PlanAccounts`,
      },
      {
        id: ContactDetailsTab.Notes,
        value: `${path}/${ContactDetailsPaths.Notes}`,
        label: formatMessage(`${i18nBasePath}.Tabs.Notes`),
        hidden: !userHasPermission(user, PERMISSIONS.CONTACTS_NOTES),
        i18nPath: `${i18nBasePath}.Tabs.Notes`,
      },
    ].filter(tab => !tab.hidden);
  };

  renderTabContent = () => {
    const {
      attachingRiskProfile,
      currentContact,
      loading,
      onAttachOmniRiskProfile,
      onAttachRiskProfile,
      onCreateRiskProfile,
      onDeleteRiskProfile,
      onEditRiskProfile,
      user,
    } = this.props;

    const currentTab = this.getCurrentTab();

    switch (currentTab) {
      case ContactDetailsTab.Comparisons: {
        return (
          <ContactDetailsComparisons
            user={user}
            currentContact={currentContact}
            comparisons={currentContact.comparisons || []}
            isComparisonsLoading={loading.comparisons}
            onOpenComparison={this.props.onViewComparison}
          />
        );
      }
      case ContactDetailsTab.Portfolios: {
        return <ContactPortfolios />;
      }
      case ContactDetailsTab.RiskProfiles: {
        return (
          <ContactDetailsRiskProfiles
            currentContact={currentContact}
            loading={attachingRiskProfile}
            onAttachRiskProfile={onAttachRiskProfile}
            onCreateRiskProfile={onCreateRiskProfile}
            onEditRiskProfile={onEditRiskProfile}
            onDeleteRiskProfile={onDeleteRiskProfile}
          />
        );
      }
      case ContactDetailsTab.OmniRiskProfiles: {
        return <ContactDetailsOmniRiskProfiles onCreateComparisonFromProfile={onAttachOmniRiskProfile} onDeleteRiskProfile={onDeleteRiskProfile} />;
      }
      case ContactDetailsTab.PlanAccounts: {
        return <ContactPlanAccounts currentContact={currentContact} />;
      }
      case ContactDetailsTab.Notes: {
        return <ContactNotes />;
      }
    }
  };

  onTabChange = (tab: ContactDetailsTab) => {
    this.setState({ currentTab: tab });
  };

  render() {
    return this.state.showSpinner ? (
      <LoadingSpinner />
    ) : (
      <div className="contact-details-tabs-sections">
        <div className="contact-details-tabs-header">
          <Tabs className="tabs-v2" onChange={this.onTabChange} selected={this.state.currentTab}>
            {this.getTabs().map(option => (
              <ControlLinkItem ariaLabel={option.label} key={option.id} route={option.value || '#'} value={option.id}>
                <FormattedMessage id={option.i18nPath} />
              </ControlLinkItem>
            ))}
          </Tabs>
        </div>
        <div className="contact-details-tabs-body">{this.renderTabContent()}</div>
      </div>
    );
  }
}

const ContactDetailsTabs: React.FC<React.ComponentProps<typeof ContactDetailsTabsV2>> = props => {
  const { isLightVersion } = useThemeVersion();

  if (isLightVersion) {
    return <ContactDetailsTabsV2 {...props} />;
  }

  return <LegacyContactDetailsTabs {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  isGettingQuestionnaires: selectIsLoadingSome(state, [GET_QUESTIONNAIRE.type]),
});

export default withRouter(connect(mapStateToProps, {})(ContactDetailsTabs));
